/**
 * The announcements list and one announcement (TMMAPP-0142 U2 item 13a), shared by Help &
 * feedback's Announcements tab and the "What's new" dialog:
 * - each item with its kind as a chip (`IMPORTANT` in the warning tone, the others neutral), its
 *   title and date; an unread one is bold and says so to a screen reader;
 * - opening one shows its body as safe Markdown (an image is its alt text) and marks it read;
 *   Back returns to the list, with focus on the item it came from.
 */
import { useEffect, useId, useRef, useState } from 'react';
import { Box, Button, List, ListItemButton, Stack, Typography, useTheme } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { useTranslation } from 'react-i18next';
import type { Announcement, AnnouncementKind } from '@/api/announcements';
import { ErrorState } from '@/components/ErrorState';
import { PreviousIcon } from '@/components/icons';
import { Pill } from '@/components/Pill';
import { SafeMarkdown } from '@/components/SafeMarkdown';
import { resolveTokens } from '@/theme/theme';
import type { AnnouncementsValue } from './announcementsContext';
import { usePublishedOn } from './usePublishedOn';

const KIND_KEYS: Record<AnnouncementKind, string> = {
  NEWS: 'support.announcements.kind.NEWS',
  FEATURE: 'support.announcements.kind.FEATURE',
  MAINTENANCE: 'support.announcements.kind.MAINTENANCE',
  IMPORTANT: 'support.announcements.kind.IMPORTANT',
};

export function AnnouncementKindChip({ kind }: { kind: AnnouncementKind }) {
  const { t } = useTranslation();
  return (
    <Box component="span" data-announcement-kind={kind}>
      {kind === 'IMPORTANT' ? (
        <Pill tone="warning">{t(KIND_KEYS[kind])}</Pill>
      ) : (
        <Pill wash="grey">{t(KIND_KEYS[kind])}</Pill>
      )}
    </Box>
  );
}

/** One announcement: its kind, title, date and body. */
export function AnnouncementBody({
  announcement,
  headingLevel = 'h3',
  headingId,
  hideTitle = false,
}: {
  announcement: Announcement;
  headingLevel?: 'h2' | 'h3';
  headingId?: string | undefined;
  /** The title is shown elsewhere (the pop-up's dialog title). */
  hideTitle?: boolean | undefined;
}) {
  const tokens = resolveTokens(useTheme());
  const publishedOn = usePublishedOn();
  return (
    <Stack sx={{ gap: 1 }} data-announcement={announcement.id}>
      <Stack direction="row" sx={{ alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <AnnouncementKindChip kind={announcement.kind} />
        <Typography component="span" sx={{ fontSize: 12, color: tokens.text2 }}>
          {publishedOn(announcement)}
        </Typography>
      </Stack>
      {!hideTitle && (
        <Typography
          component={headingLevel}
          id={headingId}
          tabIndex={-1}
          sx={{ m: 0, fontSize: 15, fontWeight: 600, overflowWrap: 'anywhere' }}
        >
          {announcement.title}
        </Typography>
      )}
      <SafeMarkdown source={announcement.body} />
    </Stack>
  );
}

export function AnnouncementList({
  announcements,
  headingLevel = 'h3',
}: {
  announcements: AnnouncementsValue;
  headingLevel?: 'h2' | 'h3';
}) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  const publishedOn = usePublishedOn();
  const [openId, setOpenId] = useState<number | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);
  const returnTo = useRef<number | null>(null);
  const bodyHeadingId = useId();
  const { items, error, reload, markRead } = announcements;
  const open = openId === null ? null : (items?.find((item) => item.id === openId) ?? null);

  // Opening moves focus to the announcement's title; Back to the item it came from.
  useEffect(() => {
    if (open !== null) {
      document.getElementById(bodyHeadingId)?.focus();
    } else if (returnTo.current !== null) {
      listRef.current
        ?.querySelector<HTMLElement>(`[data-announcement-row="${returnTo.current}"]`)
        ?.focus();
      returnTo.current = null;
    }
  }, [open, bodyHeadingId]);

  function show(item: Announcement) {
    setOpenId(item.id);
    if (!item.read) markRead(item.id);
  }

  if (open !== null) {
    return (
      <Stack sx={{ gap: 1.5 }}>
        <Box>
          <Button
            size="small"
            startIcon={<PreviousIcon />}
            onClick={() => {
              returnTo.current = open.id;
              setOpenId(null);
            }}
          >
            {t('support.announcements.back')}
          </Button>
        </Box>
        <AnnouncementBody
          announcement={open}
          headingLevel={headingLevel}
          headingId={bodyHeadingId}
        />
      </Stack>
    );
  }

  if (items === null) {
    return error !== null ? (
      <ErrorState
        variant="section"
        error={error}
        what={t('support.announcements.loadError')}
        onRetry={reload}
      />
    ) : (
      <Typography sx={{ fontSize: 14, color: tokens.text2 }}>{t('common.loading')}</Typography>
    );
  }
  if (items.length === 0) {
    return (
      <Typography sx={{ fontSize: 14, color: tokens.text2 }} data-announcements-empty="">
        {t('support.announcements.empty')}
      </Typography>
    );
  }
  return (
    <List ref={listRef} disablePadding sx={{ listStyle: 'none', m: 0 }} data-announcements="">
      {items.map((item) => (
        <Box component="li" key={item.id} sx={{ borderBottom: `1px solid ${tokens.line}` }}>
          <ListItemButton
            onClick={() => show(item)}
            data-announcement-row={item.id}
            sx={{ flexDirection: 'column', alignItems: 'flex-start', gap: 0.5, py: 1.25 }}
          >
            <Stack direction="row" sx={{ alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <AnnouncementKindChip kind={item.kind} />
              <Typography component="span" sx={{ fontSize: 12, color: tokens.text2 }}>
                {publishedOn(item)}
              </Typography>
            </Stack>
            <Typography
              component="span"
              sx={{
                fontSize: 14,
                fontWeight: item.read ? 400 : 600,
                color: tokens.text,
                overflowWrap: 'anywhere',
              }}
            >
              {item.title}
              {!item.read && (
                <Box component="span" sx={visuallyHidden}>
                  {` (${t('support.announcements.unread')})`}
                </Box>
              )}
            </Typography>
          </ListItemButton>
        </Box>
      ))}
    </List>
  );
}
